import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col, Panel, Form } from 'react-bootstrap';
import _ from 'lodash';
import TextArea from './TextArea';
import DateInput from './DateInput';
import TextInput from './TextInput';
import YesNoRadio from './YesNoRadio';
import FormField from './FormField';
import AutoComplete from './AutoComplete';
import Select from './Select';
import Button from './Button';
import Radio from './Radio';
import { isEmptyString } from '../../validators/formValidators';
import { normalizePhone, normalizeSSN, normalizeMoney } from '../../helpers/normalizers';
import { transformText,
         transformTextFromPlaceholderMapping,
         maybeSerializedJson, 
         getFullName,
         formatDate,
         formatDateTime,
         getContactFieldsMapFromConfig } from '../../helpers/utils';

import { privateFormFieldColor } from './../../helpers/colors';
import { getStatesOptions, normalizeZip, getStatesLabel } from '../../helpers/internationalization';


export const DYNAMIC_FIELD_TYPES = [
    {
        name: 'TextInput',
        label: 'Text'
    },
    {
        name: 'TextArea',
        label: 'Text (Multiline)'
    },
    {
        name: 'DateInput',
        label: 'Date',
    },
    {
        name: 'DateTimeInput',
        label: 'Date/Time'
    },
    {
        name: 'YesNoRadio',
        label: 'Yes/No'
    },
    {
        name: 'Choice',
        label: 'Multiple Choice'
    },
    {
        name: 'Separator',
        label: 'Section'
    },
    {
        name: 'State',
        label: 'State Selection'
    },
    {
        name: 'Email',
        label: 'Email'
    },
    {
        name: 'Phone',
        label: 'Phone'
    },
    {
        name: 'Fax',
        label: 'Fax'
    },
    {
        name: 'Contact',
        label: 'Contact'
    },
    {
        name: 'SSN',
        label: 'SSN'
    },
    {
        name: 'ZIP',
        label: 'ZIP'
    },
    {
        name: 'Cash',
        label: 'Money'
    },
    {
        name: 'Employee',
        label: 'Employee'
    },
    {
        name: 'URL',
        label: 'Link'
    },
    {
        name: 'CaseType',
        label: 'Case Type'
    },
    // Non-renderable fields
    {
        name: 'Hidden',
        label: 'Hidden'
    }
];


export const wrapEmployeeForSelect = (e) => {
    if (!e) {
        return {};
    }

    return {
        label: getFullName(e),
        value: e.id
    };
};


export const filterDynamicFieldsMap = (fieldsMap, publicIntake = false) => {
    if (!fieldsMap) {
        return [];
    }

    const fieldsMapFiltered = fieldsMap.filter(field => field.type !== 'Hidden' && (!field.publicHidden || !publicIntake));
    return fieldsMapFiltered;
};

export const isFieldPrivate = (field) => {
    return field.type === 'Hidden' || field.publicHidden;
};

export const getDynamicFieldsMapFromConfig = (currentConfig, type, table, addTableToName = false, configSource = 'workflow') => {
    let fieldsMap = [];
    if (!isEmptyString(type) && currentConfig) {
        let fieldsSource = currentConfig[configSource].find((value) => {return value['type'] === type;});
        if (fieldsSource && fieldsSource.tables) {
            let targetTable = fieldsSource.tables.find((value) => {return value['table'] === table;});
            fieldsMap = _.cloneDeep(targetTable.fields);
        }
    }

    if (addTableToName) {
        _.forOwn(fieldsMap, (value, key) => { 
            if (!fieldsMap[key].name || !fieldsMap[key].name.startsWith(addTableToName)) {
                fieldsMap[key].name = `${addTableToName}${value.name}`;
            }
        });
    }

    return fieldsMap;
};

export const replaceWorkflowTablesWithAggregatedTable = (currentConfig, table, aggregatedFields) => {
    if (currentConfig) {
        for (let i = 0; i < currentConfig.workflow.length; i += 1) {
            const workflow = currentConfig.workflow[i];
            if (workflow && workflow.tables) {
                let targetTable = workflow.tables.find((value) => {return value['table'] === table;});
                if (!targetTable) {
                    targetTable = { table };
                    workflow.tables.unshift(targetTable);
                }

                targetTable.fields = [];

                for (let j = 0; j < aggregatedFields.length; j += 1) {
                    const field = aggregatedFields[j];
                    if (field.visibility && (field.visibility.includes('ALL') || field.visibility.includes(workflow.type))) {
                        targetTable.fields.push(field);
                    }
                }
            }
        }
    }
};


export const getDynamicFieldsMapAggregationFromConfig = (currentConfig, table) => {
    // TODO: diff like algorithm to maintain proper ordering of various fields
    const fieldsMap = [];
    if (currentConfig) {
        for (let i = 0; i < currentConfig.workflow.length; i++) {
            const workflow = currentConfig.workflow[i];
            if (workflow && workflow.tables) {
                let targetTable = workflow.tables.find((value) => {return value['table'] === table;});
                if (!targetTable) {
                    targetTable = {
                        table,
                        fields: [],
                    };

                    workflow.tables.unshift(targetTable);
                }

                for (let j = 0; j < targetTable.fields.length; j++) {
                    let index = fieldsMap.findIndex(field => {
                        return field.name === targetTable.fields[j].name;
                    });

                    let field = { ...targetTable.fields[j] };
                    if (field.name.startsWith('client.')) {
                        field.name = field.name.replace("client.", "");
                        field['$Id$'] = field['$Id$'].replace("client.", "");
                    }
                    if (index >= 0) {
                        if (fieldsMap[index].props.required || field.props.required) {
                            fieldsMap[index].props.required = true;
                        }

                        field = fieldsMap[index];
                    } else {
                        fieldsMap.push(field);
                    }

                    if (field['visibility'] === undefined) {
                        field['visibility'] = [];
                    }

                    if (!field['visibility'].find(v => v === workflow.type)) {
                        field['visibility'].push(workflow.type);
                    }

                    if (field['visibility'].length === currentConfig.workflow.length) {
                        field['visibility'] = ['ALL'];
                    }
                }
            }
        }
    }

    return fieldsMap;
};

export const getDynamicFieldsMapAggregation = (state, table) => {
    return getDynamicFieldsMapAggregationFromConfig(state.user.config, table);
};


class DynamicFields extends PureComponent {
    constructor(props) {
        super(props);
        this.contactHandleChange = this.contactHandleChange.bind(this);
        this.setValue = this.setValue.bind(this);
        this.setDateValue = this.setDateValue.bind(this);
        this.setDateTimeValue = this.setDateTimeValue.bind(this);
        this.state = {
            editDialogOpen: false,
            choices: {},
        };
    }

    contactHandleChange(fieldName, value) {
        let newValue = this.props.contactField.value;
        if (newValue) {
            newValue = JSON.parse(newValue);
        } else {
            newValue = {}
        }

        newValue[fieldName] = value;
        const label = transformTextFromPlaceholderMapping(this.props.contactField.props.clientLabel, newValue);
        newValue['$Label$'] = label;
        this.props.setFieldValue(this.props.contactField.name, JSON.stringify(newValue));
    }

    setValue(field, value) {
        if (this.props.contactField) {
            this.contactHandleChange(field.name, value);
        } else {
            this.props.setFieldValue(field.name, value);
        }
    }

    setDateValue(field, value) {
        const dateStr = formatDate(value);
        if (this.props.contactField) {
            this.contactHandleChange(field.name, dateStr);
        } else {
            this.props.setFieldValue(field.name, dateStr);
        }
    }

    setDateTimeValue(field, value) {
        const dateTimeStr = formatDateTime(value);
        if (this.props.contactField) {
            this.contactHandleChange(field.name, dateTimeStr);
        } else {
            this.props.setFieldValue(field.name, dateTimeStr);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (!_.isEqual(this.props.fields, nextProps.fields)) {
            this.setState({choices: {}});
        }
    }

    render() {
        const {
            values,
            fieldsMap,
            submitting,
            loading,
            currentConfig,
            extraData,
            publicIntake,
            disabled,
            employees,
            handleBlur,
            handleChange,
            errors = {},
            touched = {},
            labelPrefix,
            contactField = false,
        } = this.props;

        const dialogs = [];
        const fieldsMapFiltered = filterDynamicFieldsMap(fieldsMap, publicIntake);
        const generatedFields = fieldsMapFiltered.map((currField, index) => {
            currField = _.cloneDeep(currField);
            const field = {
                ...currField, 
                onBlur: handleBlur,
                onChange: handleChange,
                value: values && values[currField.name],
                touched: touched[currField.name],
                error: errors[currField.name],
            };

            if (labelPrefix) {
                field['props']['label'] = labelPrefix + ' - ' + field['props']['label'];
            }

            if (contactField) {
                field.onChange = evt => {
                    evt.preventDefault();
                    this.contactHandleChange(field.name, evt.target.value, contactField);
                }

                field.props.required = false;
                field.onBlur = undefined;
            }

            let component = '';
            const commonProps = {
                disabled: submitting || loading || disabled,
                loading,
            };

            if (isFieldPrivate(field)) {
                field.props.label = (<span style={{ color: privateFormFieldColor }}>{field.props.label}</span>);
            }

            const props = {
                name: field.name,
                key: field.name,
                ...currField.props,
                ...commonProps,
            };

            const componentType = fieldsMapFiltered[index].type;
            let contacts = [];
            let contactValues = {};
            if (componentType === 'Contact') {
                let untransformedContacts = [];
                if (this.props.contacts) {
                    if (props.clientType === 'Client') {
                        contacts = this.props.allContacts.filter(c => c.client['ClientType'] === 'Client').map(contact => {
                            const label = transformTextFromPlaceholderMapping(props.clientLabel, contact.client);
                            contact['$Label$'] = label;
                            return {
                                label,
                                value: JSON.stringify(contact.client)
                            };
                        });
                    } else {
                        untransformedContacts = this.props.contacts.filter(contact => {
                            return contact['ClientType'] === props.clientType;
                        });

                        contacts = untransformedContacts.map(contact => {
                            const label = transformTextFromPlaceholderMapping(props.clientLabel, contact);
                            contact['$Label$'] = label;
                            return {
                                label,
                                value: JSON.stringify(contact)
                            };
                        });
                    }
                }

                if (field.value !== "{}" && !isEmptyString(field.value) && !maybeSerializedJson(field.value)) {
                    const contact = contacts.find(c => {
                        const obj = JSON.parse(c.value);
                        if (obj['$Label$'] === field.value) {
                            contactValues = obj;
                            return true;
                        }

                        return false;
                    });

                    if (contact) {
                        field.onChange(contact.value);
                    }

                } else if (maybeSerializedJson(field.value)) {
                    let obj;
                    try {
                        obj = JSON.parse(field.value);
                    } catch(e) {}

                    contactValues = obj;
                    if (obj && obj.id && this.props.contacts) {
                        const foundContact = untransformedContacts.find(v => v.id === obj.id);
                        if (!_.isEqual(foundContact, obj)) {
                            if (obj['$Label$'] && !obj['$Label$'].startsWith('(OLD) ')) {
                                obj['$Label$'] = '(OLD) ' + obj['$Label$'];
                            }

                            const objStr = JSON.stringify(obj);
                            contacts.unshift({
                                label: obj['$Label$'],
                                value: objStr
                            });

                            if (field.value !== objStr) {
                                field.onChange(objStr);
                            }
                        }
                    }
                }

            } else if (maybeSerializedJson(field.value)) {
                try {
                    let obj = JSON.parse(field.value);
                    if (_.isObject(obj) && obj['$Label$']) {
                        field.onChange(obj['$Label$']);
                    }

                } catch (e) {}
            }

            switch (componentType) {
            case 'TextInput':
                component = (
                    <TextInput field={field}
                                {...props}
                    />
                );

                break;

            case 'TextArea':
                component = (
                    <TextArea field={field}
                              rows={10}
                              {...props}
                    />
                );

                break;

            case 'DateInput':
                field.onChange = value => this.setDateValue(field, value);
                component = (
                    <DateInput field={field}
                               {...props}
                    />
                );

                break;

            case 'DateTimeInput':
                field.onChange = value => this.setDateTimeValue(field, value);
                component = (
                    <DateInput field={field}
                               {...props}
                               time
                    />
                );

                break;

            case 'YesNoRadio':
                component = (
                    <YesNoRadio field={field}
                                {...props}/>
                );

                break;

            case 'State':
                field.onChange = value => this.setValue(field, value);
                component = (
                    <AutoComplete field={field}
                                   {...props}
                                   label={getStatesLabel(this.props.country)}
                                   options={getStatesOptions(this.props.country)}
                                   allowNew={false}
                    />
                );

                break;

            case 'Email':
                component = (
                    <TextInput field={field}
                               placeholder='email@example.com'
                               {...props}/>
                );

                break;

            case 'Phone':
            case 'Fax':
                component = (
                    <TextInput field={field}
                               {...props}
                               onChange={(evt) => {
                                   let value = normalizePhone(evt.target.value, field.value);
                                   evt.target.value = value;
                               }}
                    />
                );

                break;

            case 'SSN':
                component = (
                    <TextInput field={field}
                               {...props}
                               onChange={(evt) => {
                                   let value = normalizeSSN(evt.target.value, field.value);
                                   evt.target.value = value;
                               }}
                    />
                );

                break;

            case 'ZIP':
                component = (
                    <TextInput field={field}
                               {...props}
                               onChange={(evt) => {
                                   let value = normalizeZip(evt.target.value, field.value, this.props.country);
                                   evt.target.value = value;
                               }}
                    />
                );

                break;

            case 'Cash':
                component = (
                    <TextInput field={field}
                                {...props}
                                onChange={(evt) => {
                                    let value = normalizeMoney(evt.target.value, field.value);
                                    evt.target.value = value;
                                }}/>
                );

                break;

            case 'URL':
                let url = props.url || '';
                let target = props.target;
                if (extraData && currentConfig && !publicIntake) {
                    try {
                        url = transformText(url, currentConfig, extraData);
                    } catch (err) {}
                }

                component = (
                    <FormField field={field} {...props}>
                    <Button onClick={(evt) => {
                                    evt.preventDefault();
                                    window.open(url, target);
                                }}
                            bsStyle='link'
                            disabled={loading || submitting || disabled}
                            label="Open"/>
                    </FormField>
                );

                break;

            case 'Separator':
                component = (
                    <div style={{textAligh: 'center'}}><hr style={{margin: 15}}/><h5><b>{props.label}</b></h5><hr style={{margin: 15}}/></div>
                );

                break;

            case 'Choice':
                if (props.customEnabled) {
                    field.onChange = value => this.setValue(field, value);
                    component = (
                        <AutoComplete field={field}
                                    {...props}
                                    options={props.options}
                                    allowNew={props.customEnabled}/>
                    );

                } else {
                    component = (
                        <Select field={field}
                                {...props}
                                options={props.options}/>
                    );
                }

                break;

            case 'Employee':
                let options = [];
                if (employees) {
                    options = employees.map(wrapEmployeeForSelect);
                }

                options.unshift('');
                component = (
                    <Select field={field}
                            {...props}
                            options={options}/>
                );

                break;

            case 'Contact':
                const radioFieldName = field.name + '_SelectionRadio';
                const label = field.props.label;
                if (!this.state.choices[radioFieldName] || this.state.choices[radioFieldName] === 'existing') {
                    field.onChange = value => this.setValue(field, value);
                    if (_.isString(field.value)) {
                        try {
                            const obj = JSON.parse(field.value);
                            field.onChange({
                                label: obj['$Label$'],
                                value: field.value
                            });
                        } catch (error) {
                            field.onChange(undefined);
                        }
                    }
                }

                if (field.props.clientNewAllowed && !this.props.publicIntake) {
                    component = (
                        <fieldset style={{
                            border: '1px solid #ddd',
                            paddingLeft: '15px',
                            paddingRight: '15px',
                            marginBottom: 15,
                            width: 'auto'
                        }}>
                        <legend style={{
                            width: '220px',
                            border: 'none'
                        }}> 
                        <Form inline style={{paddingLeft: 10}}>
                        <label>{label}:</label>
                        <Radio
                            field={{
                                name: radioFieldName,
                                onChange: (evt) => {
                                    const choices = {...this.state.choices};
                                    choices[radioFieldName] = evt.target.value;
                                    if (choices[radioFieldName] === 'new') {
                                        field.onChange("{}");
                                    }
                                    this.setState({ choices });
                                },
                                value: this.state.choices[radioFieldName] || 'existing'
                            }}
                            options={[
                                {label: 'Select', value: 'existing'},
                                {label: 'Create', value: 'new'}
                            ]}
                            style={{
                                marginTop: 3,
                                marginLeft: 20,
                            }}
                        />
                        </Form>
                        </legend>
                        <Row style={{ marginTop: -10 }}>
                        {this.state.choices[radioFieldName] === 'new' ? (
                            <DynamicFields
                                contactField={field}
                                values={contactValues}
                                errors={this.props.errors}
                                touched={this.props.touched}
                                handleChange={this.contactHandleChange}
                                fieldsMap={this.props.contactFieldsMap[field.props.clientType] || []}
                                submitting={this.props.isSubmitting}
                                loading={this.props.loadingCurrent}
                                currentConfig={this.props.currentConfig}
                                publicIntake={this.props.publicIntake}
                                setFieldValue={this.props.setFieldValue}
                                country={this.props.country}
                            />
                        ) : (
                            <AutoComplete
                                field={field}
                                {...props}
                                label=''
                                options={contacts}
                                isClearable={!field.props.required}
                                allowNew={false}
                                returnSelectedObject
                            />
                        )}
                        </Row>
                        </fieldset>
                    );
                } else {
                    field.onChange = value => this.setValue(field, value);
                    component = (
                        <AutoComplete
                            field={field}
                            {...props}
                            options={contacts}
                            isClearable={!field.props.required}
                            allowNew={false}
                            returnSelectedObject
                        />
                    );
                }

                break;

            default:
                debugger;
                console.error('Invalid component type ' + componentType);
                break;
            }

            return (
                <Row key={props.key + index}>
                    <Col xs={12}>
                    {component}
                    </Col>
                </Row>
            );
        });
    
        return (
            <div key={'DynamicFields'}>
                {generatedFields}
                {dialogs}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const currentConfig = state.user.config;
    const currentCase = state.cases.current;
    const currentCaseType = currentCase && currentCase.intake && currentCase.intake['CaseType'];
    const contactFieldsMap = getContactFieldsMapFromConfig(currentConfig, currentCaseType);
    return {
        currentConfig,
        contacts: state.clients.contacts,
        allContacts: state.clients.rows || [],
        employees: state.user.employees,
        currentCaseType,
        contactFieldsMap,
    }
};

DynamicFields.propTypes = {
    fields: PropTypes.object,
    currentConfig: PropTypes.object,
    extraData: PropTypes.object,
    publicIntake: PropTypes.bool,
    clientType: PropTypes.string,
    labelPrefix: PropTypes.string,
    contactField: PropTypes.object,
    country: PropTypes.string.isRequired,
};

export default connect(mapStateToProps)(DynamicFields);
